import { FaDiscord } from 'react-icons/fa'
import { MdOutlineMonitorHeart } from 'react-icons/md'
import { motion } from 'framer-motion'
import { Button, Box, Typography, Link, Stack, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

export function Footer() {
  const { t } = useTranslation()

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        sx={{
          width: '95%',
          maxWidth: '900px',
          mx: 'auto',
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={4} 
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#fff', 
                fontWeight: 800,
                fontSize: '1.5rem',
                letterSpacing: '-0.5px',
                background: 'linear-gradient(135deg, #fff 0%, #a78bfa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              PYRENZAI
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#6b7280', 
                display: 'block',
                mt: 0.5,
              }}
            >
              {t('messages.create')}. {t('messages.chat')}. {t('messages.connect')}.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
            <Box>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#9ca3af', 
                  fontWeight: 600, 
                  mb: 1.5,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {t('footer.legal')}
              </Typography>
              <Stack spacing={1}>
                <Link 
                  href="/Policy" 
                  sx={{ 
                    color: '#6b7280', 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#fff' },
                  }}
                >
                  {t('footer.privacyPolicy')}
                </Link>
                <Link 
                  href="/ContentPolicy" 
                  sx={{ 
                    color: '#6b7280', 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#fff' },
                  }}
                >
                  {t('footer.contentRemoval')}
                </Link>
              </Stack>
            </Box>

            <Box>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#9ca3af', 
                  fontWeight: 600, 
                  mb: 1.5,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {t('footer.resources')}
              </Typography>
              <Stack spacing={1}>
                <Link 
                  href="/Themes" 
                  sx={{ 
                    color: '#6b7280', 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#fff' },
                  }}
                >
                  {t('footer.themeStore')}
                </Link>
                <Link 
                  href="/docs" 
                  sx={{ 
                    color: '#6b7280', 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#fff' },
                  }}
                >
                  {t('footer.documentation')}
                </Link>
                <Link
                  href="https://pyrenzai.userjot.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#6b7280', 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#fff' },
                  }}
                >
                  {t('footer.feedback')}
                </Link>
                <Link
                  href="https://pyrenzai.userjot.com/updates"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#6b7280', 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#fff' },
                  }}
                >
                  {t('footer.changelog')}
                </Link>
              </Stack>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.06)' }} />

        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          justifyContent="space-between" 
          alignItems="center"
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#4b5563', 
              fontSize: '0.75rem',
            }}
          >
            © 2025 Pyrenz AI. {t('messages.allRightsReserved')}
          </Typography>

          <Stack direction="row" spacing={1.5}>
            <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
              <Button
                component="a"
                href="https://discord.gg/zTcyP4WB8h"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                startIcon={<FaDiscord size={16} />}
                sx={{
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  backgroundColor: 'rgba(88, 101, 242, 0.15)',
                  border: '1px solid rgba(88, 101, 242, 0.25)',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.5,
                  minWidth: 'auto',
                  '&:hover': { 
                    backgroundColor: 'rgba(88, 101, 242, 0.25)',
                  },
                }}
              >
                {t('footer.discord')}
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
              <Button
                component="a"
                href="https://stats.uptimerobot.com/BCu4M0Cgjf"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                startIcon={<MdOutlineMonitorHeart size={16} />}
                sx={{
                  color: '#10b981',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.5,
                  minWidth: 'auto',
                  '&:hover': { 
                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                  },
                }}
              >
                {t('footer.status')}
              </Button>
            </motion.div>
          </Stack>
        </Stack>
      </Box>
    </motion.div>
  )
}